import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const DealForm = ({ deal, contacts, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    clientName: '',
    dealValue: '',
    expectedCloseDate: '',
    salesRep: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (deal) {
      setFormData({
        clientName: deal?.clientName || '',
        dealValue: deal?.dealValue || '',
        expectedCloseDate: deal?.expectedCloseDate || '',
        salesRep: deal?.salesRep || ''
      });
    }
  }, [deal]);

  const contactOptions = contacts?.map(contact => ({
    value: contact?.name,
    label: contact?.name,
    description: contact?.company
  }));

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.clientName?.trim()) {
      newErrors.clientName = 'Client name is required';
    }

    if (!formData?.dealValue) {
      newErrors.dealValue = 'Deal value is required';
    } else if (parseFloat(formData?.dealValue) <= 0) {
      newErrors.dealValue = 'Deal value must be greater than 0';
    }

    if (!formData?.expectedCloseDate) {
      newErrors.expectedCloseDate = 'Expected close date is required';
    } else {
      const selectedDate = new Date(formData.expectedCloseDate);
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.expectedCloseDate = 'Expected close date cannot be in the past';
      }
    }

    if (!formData?.salesRep?.trim()) {
      newErrors.salesRep = 'Sales representative name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSave({
        ...deal,
        clientName: formData?.clientName,
        dealValue: parseFloat(formData?.dealValue),
        expectedCloseDate: formData?.expectedCloseDate,
        salesRep: formData?.salesRep
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const getTodayDate = () => {
    const today = new Date();
    return today?.toISOString()?.split('T')?.[0];
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
      <div>
        <Select
          label="Client Name"
          description="Select existing contact or type new name"
          placeholder="Choose or enter client name"
          options={contactOptions}
          value={formData?.clientName}
          onChange={(value) => handleInputChange('clientName', value)}
          error={errors?.clientName}
          required
          searchable
          clearable
        />
      </div>
      <div>
        <Input
          label="Deal Value"
          type="number"
          placeholder="Enter deal value"
          value={formData?.dealValue}
          onChange={(e) => handleInputChange('dealValue', e?.target?.value)}
          error={errors?.dealValue}
          description={formData?.dealValue ? formatCurrency(formData?.dealValue) : 'Enter amount in INR'}
          required
          min="0"
          step="0.01"
        />
      </div>
      <div>
        <Input
          label="Expected Close Date"
          type="date"
          placeholder="Select expected close date"
          value={formData?.expectedCloseDate}
          onChange={(e) => handleInputChange('expectedCloseDate', e?.target?.value)}
          error={errors?.expectedCloseDate}
          description="Select the anticipated deal closure date"
          required
          min={getTodayDate()}
        />
      </div>
      <div>
        <Input
          label="Sales Representative"
          type="text"
          placeholder="Enter sales rep name"
          value={formData?.salesRep}
          onChange={(e) => handleInputChange('salesRep', e?.target?.value)}
          error={errors?.salesRep}
          description="Assign a sales representative to this deal"
          required
        />
      </div>
      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 md:pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          fullWidth
          className="sm:flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          loading={isLoading}
          iconName="Save"
          iconPosition="left"
          fullWidth
          className="sm:flex-1"
        >
          Save Changes
        </Button>
      </div>
    </form>
  );
};

export default DealForm;